'use client'

import useAuthStore from '@/store/useAuthStore';
import supabase from './supabase';


export interface ElementStatus {
  user_id: string;
  quest_id: string;
  chapter_id: string;
  lesson_id: string;
  element_id: string;
  finished: boolean;
  data?: any; // optional JSON data
  created_at?: string;
  updated_at?: string;
}

export interface ElementStatusQueryParams {
  user_id?: string;
  quest_id?: string;
  chapter_id?: string;
  lesson_id?: string;
  element_id?: string;
}

export interface SubElementStatus {
  element_id: string;
  sub_element_id: string;
  finished: boolean;
  finish_at?: string;
}

/**
 * Service for managing element status data in Supabase
 */
export class ElementStatusService {
  /**
   * Upsert element status (insert if not exists, update if exists)
   */
  static async updateElementStatus(
    elementStatus: Omit<ElementStatus, 'created_at' | 'updated_at'>
  ): Promise<ElementStatus | null> {
    try {
      const now = new Date().toISOString();

      // Look for an existing record
      const { data: existingData } = await supabase
        .from('element_status')
        .select('*')
        .eq('user_id', elementStatus.user_id)
        .eq('quest_id', elementStatus.quest_id)
        .eq('chapter_id', elementStatus.chapter_id)
        .eq('lesson_id', elementStatus.lesson_id)
        .eq('element_id', elementStatus.element_id)
        .single();

      if (existingData) {
        // Update existing record
        const newData = { ...existingData.data, ...elementStatus.data };
        const { data, error } = await supabase
          .from('element_status')
          .update({
            finished: elementStatus.finished,
            data: newData,
            updated_at: now,
            quest_id: elementStatus.quest_id,
            chapter_id: elementStatus.chapter_id,
            lesson_id: elementStatus.lesson_id,
          })
          .eq('user_id', elementStatus.user_id)
          .eq('quest_id', elementStatus.quest_id)
          .eq('chapter_id', elementStatus.chapter_id)
          .eq('lesson_id', elementStatus.lesson_id)
          .eq('element_id', elementStatus.element_id)
          .select()
          .single();

        if (error) {
          console.error('Error updating element status:', error);
          return null;
        }

        return data as ElementStatus;
      } else {
        // Insert new record
        const { data, error } = await supabase
          .from('element_status')
          .insert({
            ...elementStatus,
            created_at: now,
            updated_at: now
          })
          .select()
          .single();

        if (error) {
          console.error('Error inserting element status:', error);
          return null;
        }

        return data as ElementStatus;
      }
    } catch (error) {
      console.error('Error in updateElementStatus:', error);
      return null;
    }
  }

  /**
   * Get element status by specific IDs
   */
  static async getElementStatus(
    params: ElementStatusQueryParams
  ): Promise<ElementStatus[]> {
    try {
      let query = supabase
        .from('element_status')
        .select('*');

      // Add filters based on provided parameters
      if (params.user_id) {
        query = query.eq('user_id', params.user_id);
      }

      if (params.quest_id) {
        query = query.eq('quest_id', params.quest_id);
      }

      if (params.chapter_id) {
        query = query.eq('chapter_id', params.chapter_id);
      }

      if (params.lesson_id) {
        query = query.eq('lesson_id', params.lesson_id);
      }

      if (params.element_id) {
        query = query.eq('element_id', params.element_id);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching element status:', error);
        return [];
      }

      return data as ElementStatus[];
    } catch (error) {
      console.error('Error in getElementStatus:', error);
      return [];
    }
  }

  /**
   * Check if a specific element is finished
   */
  static async isElementFinished(
    user_id: string,
    quest_id: string,
    chapter_id: string,
    lesson_id: string,
    element_id: string
  ): Promise<boolean> {
    try {
      const { data } = await supabase
        .from('element_status')
        .select('finished')
        .eq('user_id', user_id)
        .eq('quest_id', quest_id)
        .eq('chapter_id', chapter_id)
        .eq('lesson_id', lesson_id)
        .eq('element_id', element_id)
        .single();

      return data?.finished || false;
    } catch (error) {
      return false;
    }
  }

  static async getFinishedSubElements(element_id: string): Promise<SubElementStatus[]> {
    const user = useAuthStore.getState().user;
    if (!user) {
      return [];
    }
    try {
      const { data, error } = await supabase
        .from('sub_element_status')
        .select('element_id, sub_element_id, finished, updated_at')
        .eq('user_id', user.id)
        .eq('element_id', element_id);
      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching finished sub elements:', error);
        return [];
      }
      if (!data) {
        return [];
      }
      return data as SubElementStatus[];
    } catch (error) {
      console.error('Error in getFinishedSubElements:', error);
      return [];
    }
  }

  static async updateSubElementStatus(element_id: string, sub_element_id: string, element_type: string, finished: boolean): Promise<void> {
    try {
      const user = useAuthStore.getState().user;
      if (!user) {
        return;
      }

      const { data, error } = await supabase
        .from('sub_element_status')
        .upsert({
          element_id: element_id,
          sub_element_id: sub_element_id,
          user_id: user.id,
          element_type: element_type,
          finished: finished
        })
        .eq('element_id', element_id)
        .eq('sub_element_id', sub_element_id)
        .eq('user_id', user.id);
      if (error) {
        console.error('Error updating sub element status:', error);
        return;
      }
    } catch (error) {
      console.error('Error in updateSubElementStatus:', error);
    }
  }

  /**
   * Get all finished elements for a given scope (quest, chapter, or lesson)
   */
  static async getFinishedElements(params: ElementStatusQueryParams): Promise<ElementStatus[]> {
    if (!params.user_id) {
      return [];
    }
    try {
      let query = supabase
        .from('element_status')
        .select('*')
        .eq('finished', true)
        .eq('user_id', params.user_id);


      if (params.quest_id) {
        query = query.eq('quest_id', params.quest_id);
      }

      if (params.chapter_id) {
        query = query.eq('chapter_id', params.chapter_id);
      }

      if (params.lesson_id) {
        query = query.eq('lesson_id', params.lesson_id);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching finished elements:', error);
        return [];
      }

      return data as ElementStatus[];
    } catch (error) {
      console.error('Error in getFinishedElements:', error);
      return [];
    }
  }

  /**
   * Load and sync all finished elements for a user's quest into local state
   * This is useful for initialization to ensure local state matches database state
   */
  static async loadFinishedElementsForLesson(
    user_id: string,
    lesson_id: string
  ): Promise<Record<string, { finished: boolean, data?: any }>> {
    try {
      const { data, error } = await supabase
        .from('element_status')
        .select('element_id, finished, data')
        .eq('user_id', user_id)
        .eq('lesson_id', lesson_id)
        .eq('finished', true);

      if (error) {
        console.error('Error loading finished elements:', error);
        return {};
      }

      // Convert to a map of element_id -> { finished, data }
      const finishedElementsMap: Record<string, { finished: boolean, data?: any }> = {};
      data.forEach(item => {
        finishedElementsMap[item.element_id] = {
          finished: item.finished,
          data: item.data
        };
      });

      return finishedElementsMap;
    } catch (error) {
      console.error('Error in loadFinishedElementsForLesson:', error);
      return {};
    }
  }
}

export default ElementStatusService; 
